"use client";

import { useCallback, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Camera } from "lucide-react";
import { ScreenshotAnnotator } from "./screenshot-annotator";
import {
  feedbackTypeKeys,
  type ClassMerge,
  type FeedbackApiFetch,
  type FeedbackComponents,
  type FeedbackIdentity,
  type FeedbackMessages,
  type FeedbackMode,
  type FeedbackPayload,
  type FeedbackSessionUser,
  type FeedbackTypeKey,
} from "./types";
import {
  getCapturedConsoleErrors,
  getCapturedNetworkErrors,
  getPerformanceMetrics,
} from "./use-global-error-capture";

const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_IMAGE_SIZE = 5 * 1024 * 1024;
const MAX_UPLOADS = 3;

function userDisplayName(user: FeedbackSessionUser | null, fallback: string): string {
  if (!user) return fallback;
  const first = typeof user.firstName === "string" ? user.firstName : "";
  const last = typeof user.lastName === "string" ? user.lastName : "";
  const full = `${first} ${last}`.trim();
  if (full) return full;
  if (typeof user.email === "string" && user.email) return user.email;
  return fallback;
}

/** The HTTP status of a failed apiFetch call, when the host's error carries one. */
function errorStatus(err: unknown): number | null {
  if (typeof err === "object" && err && typeof (err as { status?: unknown }).status === "number") {
    return (err as { status: number }).status;
  }
  return null;
}

/** The message to show when the report could not be sent. */
function submitErrorMessage(err: unknown, messages: FeedbackMessages): string {
  const status = errorStatus(err);
  if (status === 429) return messages.rateLimited;
  if (status === 401) return messages.sessionExpired;
  return messages.submitFailed;
}

function readUserId(user: FeedbackSessionUser | null): string | null {
  if (!user) return null;
  if (typeof user.userId === "string") return user.userId;
  if (typeof user.id === "string") return user.id;
  return null;
}

export interface FeedbackFormProps {
  mode: FeedbackMode;
  user: FeedbackSessionUser | null;
  messages: FeedbackMessages;
  apiFetch: FeedbackApiFetch;
  components: FeedbackComponents;
  cn: ClassMerge;
  /** Portal mode only — link to send users when session is unexpectedly missing. */
  loginUrl?: string;
  /** Optional initial type slug (e.g. from `pontimed:open-feedback` event). */
  initialType?: string | null;
  /** Optional initial description prefill. */
  initialDescription?: string;
  /** Called after the success animation completes (after ~2s). */
  onSubmitted?: () => void;
}

/**
 * Inner feedback form — type chips, description textarea, identity selector
 * (mode-aware), screenshot capture/annotate, image upload, submit.
 *
 * No sheet shell — wrapped by <FeedbackSheet>. Lives separately so a host
 * could embed the form inline (e.g. portal /help page).
 */
export function FeedbackForm({
  mode,
  user,
  messages,
  apiFetch,
  components,
  cn,
  loginUrl,
  initialType = null,
  initialDescription = "",
  onSubmitted,
}: FeedbackFormProps) {
  const { Button, Input } = components;

  const [type, setType] = useState<string | null>(initialType);
  const [description, setDescription] = useState(initialDescription);
  const [email, setEmail] = useState("");
  const [identity, setIdentity] = useState<FeedbackIdentity>(
    mode === "portal" ? "account" : user ? "account" : "anonymous",
  );
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [screenshotData, setScreenshotData] = useState<string | null>(null);
  const [capturingScreenshot, setCapturingScreenshot] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [annotating, setAnnotating] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const captureScreenshot = useCallback(async () => {
    setCapturingScreenshot(true);
    try {
      const html2canvas = (await import("html2canvas-pro")).default;
      const canvas = await html2canvas(document.body, {
        scale: 0.5,
        logging: false,
        useCORS: true,
        ignoreElements: (el) => el.closest("[data-pontimed-sheet]") !== null,
      });
      setScreenshotData(canvas.toDataURL("image/jpeg", 0.7));
    } catch {
      setScreenshotData(null);
    }
    setCapturingScreenshot(false);
  }, []);

  const handleScreenshotToggle = useCallback(
    async (enabled: boolean) => {
      if (enabled) {
        await captureScreenshot();
      } else {
        setScreenshotData(null);
      }
    },
    [captureScreenshot],
  );

  const handleImageUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setUploadError(null);
      const files = e.target.files;
      if (!files) return;

      const remaining = MAX_UPLOADS - uploadedImages.length;
      if (remaining <= 0) {
        setUploadError(messages.maxUploadsReached);
        return;
      }

      const filesToProcess = Array.from(files).slice(0, remaining);

      for (const file of filesToProcess) {
        if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
          setUploadError(messages.invalidImageType);
          continue;
        }
        if (file.size > MAX_IMAGE_SIZE) {
          setUploadError(messages.imageTooLarge);
          continue;
        }

        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result as string;
          setUploadedImages((prev) => (prev.length < MAX_UPLOADS ? [...prev, dataUrl] : prev));
        };
        reader.readAsDataURL(file);
      }

      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [uploadedImages.length, messages],
  );

  const removeUploadedImage = useCallback((index: number) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
    setUploadError(null);
  }, []);

  // Portal mode without a session = render fallback link only.
  //
  // MUST stay below every hook. It used to sit right after the useState block,
  // above the four useCallbacks — so an anonymous portal visitor rendered 12
  // hooks and the same component rendered 16 the moment `user` arrived, which
  // is exactly the "Rendered more hooks than during the previous render"
  // crash. Signing in with the feedback sheet open took the widget down.
  if (mode === "portal" && !user) {
    return (
      <div className="py-2 text-center">
        <p className="text-muted-foreground text-sm">
          {messages.signInToSendFeedback ?? "Sign in to send feedback"}
        </p>
        {loginUrl && (
          <a
            href={loginUrl}
            className="text-primary mt-2 inline-block text-sm font-medium underline underline-offset-2"
          >
            {messages.identity.account}
          </a>
        )}
      </div>
    );
  }

  const handleSubmit = async () => {
    if (!type || !description.trim()) return;
    setSubmitting(true);
    setSubmitError(null);

    const userId = readUserId(user);
    const payload: FeedbackPayload = {
      type:
        type === "idea" ? "feature_request" : type === "incorrect_data" ? "content_issue" : type,
      description,
      userId: identity === "account" && userId ? userId : null,
      userEmail:
        identity === "account"
          ? ((typeof user?.email === "string" ? user.email : null) ?? null)
          : identity === "email"
            ? email || null
            : null,
      userType:
        identity === "anonymous"
          ? "anonymous"
          : identity === "account" && user
            ? "user"
            : "anonymous",
      pageUrl: typeof window !== "undefined" ? window.location.href : null,
      pageTitle: typeof document !== "undefined" ? document.title : null,
      locale: typeof navigator !== "undefined" ? navigator.language : null,
      device:
        typeof navigator !== "undefined"
          ? {
              userAgent: navigator.userAgent,
              screenSize: `${window.innerWidth}x${window.innerHeight}`,
            }
          : null,
      consoleErrors: getCapturedConsoleErrors(),
      networkErrors: getCapturedNetworkErrors(),
      performance: getPerformanceMetrics(),
      screenshots: [...(screenshotData ? [screenshotData] : []), ...uploadedImages],
    };

    try {
      await apiFetch.post("/api/feedback", payload);
      setSubmitted(true);
      setTimeout(() => {
        onSubmitted?.();
        setSubmitted(false);
        setType(null);
        setDescription("");
        setEmail("");
        setScreenshotData(null);
        setUploadedImages([]);
        setUploadError(null);
      }, 2000);
    } catch (err) {
      // Keep the form as typed so the person can retry; say why it failed.
      setSubmitError(submitErrorMessage(err, messages));
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="py-4 text-center">
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          className="text-primary mx-auto"
        >
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
          <path d="m9 11 3 3L22 4" />
        </svg>
        <p className="mt-2 font-medium">{messages.thankYou}</p>
        <p className="text-muted-foreground text-sm">{messages.submitted}</p>
      </div>
    );
  }

  // mode === "portal" → identity is forced "account", selector hidden.
  const showIdentitySelector = mode === "web";
  // mode === "web" → "account" only available when user is signed-in.
  const identityOptions: FeedbackIdentity[] =
    mode === "web"
      ? user
        ? ["account", "email", "anonymous"]
        : ["email", "anonymous"]
      : ["account"];

  return (
    <div className="flex-1 space-y-3 overflow-y-auto">
      <div>
        <p className="text-muted-foreground mb-1.5 text-xs font-medium">{messages.typeQuestion}</p>
        <div className="flex flex-wrap gap-1.5">
          {feedbackTypeKeys.map((key: FeedbackTypeKey) => (
            <button
              key={key}
              type="button"
              onClick={() => setType(key)}
              className={`rounded-lg border px-2.5 py-1 text-xs font-medium transition-all ${
                type === key
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-border text-muted-foreground hover:border-border/80 hover:text-foreground"
              }`}
            >
              {messages.types[key]}
            </button>
          ))}
        </div>
      </div>

      <textarea
        placeholder={messages.descriptionPlaceholder}
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        className="border-input bg-background focus:ring-primary/30 focus:border-primary w-full rounded-lg border px-3 py-2 text-base outline-none focus:ring-2 focus:ring-inset md:text-sm"
        rows={3}
      />

      {showIdentitySelector ? (
        <div>
          <div className="mb-2 flex items-center gap-2">
            <span className="text-muted-foreground shrink-0 text-[0.6875rem] font-medium">
              {messages.sendAs}
            </span>
            <div className="border-border bg-muted/40 inline-flex rounded-lg border p-0.5">
              {identityOptions.map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setIdentity(mode)}
                  className={cn(
                    "rounded-md px-2.5 py-1 text-[0.6875rem] font-medium transition-all",
                    identity === mode
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {messages.identity[mode]}
                </button>
              ))}
            </div>
          </div>
          <AnimatePresence mode="wait">
            {identity === "account" && user && (
              <motion.div
                key="account"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <div className="bg-muted/30 border-border rounded-lg border p-2.5">
                  <p className="text-xs font-medium">
                    {userDisplayName(user, messages.identity.user)}
                  </p>
                  {typeof user.email === "string" && user.email && (
                    <p className="text-muted-foreground text-[0.6875rem]">{user.email}</p>
                  )}
                  {!user.email && typeof user.phone === "string" && (
                    <p className="text-muted-foreground text-[0.6875rem]">{user.phone}</p>
                  )}
                  <p className="text-muted-foreground mt-0.5 text-[0.625rem]">
                    {messages.identity.accountNote}
                  </p>
                </div>
              </motion.div>
            )}
            {identity === "email" && (
              <motion.div
                key="email"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                className="overflow-hidden"
              >
                <Input
                  placeholder={messages.emailPlaceholder}
                  value={email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                  type="email"
                  className="text-xs"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      ) : (
        // Portal mode — small "Sending as" badge.
        user && (
          <div className="bg-muted/30 border-border rounded-lg border p-2.5">
            <p className="text-muted-foreground text-[0.6875rem]">
              {messages.sendingAs ?? messages.sendAs}{" "}
              <span className="text-foreground font-medium">
                {typeof user.email === "string" && user.email
                  ? user.email
                  : userDisplayName(user, messages.identity.user)}
              </span>
            </p>
          </div>
        )
      )}

      {!screenshotData && (
        <button
          type="button"
          onClick={() => handleScreenshotToggle(true)}
          disabled={capturingScreenshot}
          className="border-border text-muted-foreground hover:border-border/80 hover:text-foreground flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed px-3 py-2 text-xs transition-colors disabled:opacity-60"
        >
          {capturingScreenshot ? (
            <>
              <span className="border-primary h-3 w-3 animate-spin rounded-full border-2 border-t-transparent" />
              {messages.capturingScreenshot}
            </>
          ) : (
            <>
              <Camera className="size-3.5" />
              {messages.screenshotToggle}
            </>
          )}
        </button>
      )}
      {screenshotData && (
        <div className="flex items-start gap-1.5">
          <div className="relative">
            {}
            <img
              src={screenshotData}
              alt="Screenshot"
              className="border-border h-12 w-12 cursor-pointer rounded-md border object-cover transition-opacity hover:opacity-80"
              onClick={() => setAnnotating(true)}
              title={messages.annotateScreenshot}
            />
            <button
              type="button"
              onClick={() => setScreenshotData(null)}
              className="bg-background/80 text-muted-foreground hover:text-foreground absolute -top-1 -right-1 rounded-full p-0.5 backdrop-blur-sm"
              aria-label="Remove screenshot"
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M18 6 6 18M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-muted-foreground text-[0.625rem]">
              {messages.screenshotAttached}
            </span>
            <button
              type="button"
              onClick={() => setAnnotating(true)}
              className="text-primary text-[0.625rem] font-medium underline underline-offset-2"
            >
              {messages.annotateScreenshot}
            </button>
          </div>
        </div>
      )}
      {annotating && screenshotData && (
        <ScreenshotAnnotator
          imageData={screenshotData}
          onSave={(annotated) => {
            setScreenshotData(annotated);
            setAnnotating(false);
          }}
          onCancel={() => setAnnotating(false)}
        />
      )}

      <div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          multiple
          onChange={handleImageUpload}
          className="hidden"
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadedImages.length >= MAX_UPLOADS}
          className="border-border text-muted-foreground hover:border-border/80 hover:text-foreground flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed px-3 py-2 text-xs transition-colors disabled:opacity-40"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <polyline points="17 8 12 3 7 8" />
            <line x1="12" y1="3" x2="12" y2="15" />
          </svg>
          {messages.uploadImages} ({uploadedImages.length}/{MAX_UPLOADS})
        </button>
        {uploadError && <p className="text-destructive mt-1 text-[0.6875rem]">{uploadError}</p>}
        {uploadedImages.length > 0 && (
          <div className="mt-1.5 flex gap-1.5">
            {uploadedImages.map((img, i) => (
              <div key={i} className="relative">
                {}
                <img
                  src={img}
                  alt={`Upload ${i + 1}`}
                  className="border-border h-12 w-12 rounded-md border object-cover"
                />
                <button
                  type="button"
                  onClick={() => removeUploadedImage(i)}
                  className="bg-background/80 text-muted-foreground hover:text-foreground absolute -top-1 -right-1 rounded-full p-0.5 backdrop-blur-sm"
                  aria-label={`Remove image ${i + 1}`}
                >
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {submitError && (
        <p role="alert" className="text-destructive text-xs">
          {submitError}
        </p>
      )}

      <Button
        onClick={handleSubmit}
        disabled={!type || !description.trim() || submitting}
        className="w-full"
        size="sm"
      >
        {submitting ? messages.submitting : messages.submit}
      </Button>
    </div>
  );
}
